import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Bot, ChevronRight, Keyboard, LogOut, Settings, Sparkles } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import LanguageSwitcher from '../LanguageSwitcher';
import TutorialCenter from '../tutorials/TutorialCenter';
import { useBotCreditsQuery } from '../../hooks/queries';

const fmt = (n, lang) => new Intl.NumberFormat(lang, { maximumFractionDigits: 0 }).format(Math.max(0, Math.floor(n)));

/** Plan, bot credits and limits in one quiet card. */
const PlanCard = ({ plan, isFree, trialing, usage, onItemClick }) => {
  const { t, i18n } = useTranslation();
  const { data: credits } = useBotCreditsQuery();
  const total = credits ? (credits.allowance || 0) + (credits.extra || 0) : 0;
  const share = credits && total > 0 ? Math.min(100, (credits.remaining / total) * 100) : 0;
  return (
    <Link to="/app/subscription" onClick={onItemClick} data-testid="sidebar-plan"
      className="block rounded-xl border border-border bg-card px-3 py-2.5 hover:border-input transition-colors">
      <div className="flex items-center justify-between gap-2 text-xs">
        <span className="font-medium text-foreground capitalize truncate">
          {t('nav.plan_name', { plan })}{trialing ? ` · ${t('nav.trial')}` : ''}
        </span>
        {isFree ? (
          <span className="text-[11px] font-semibold text-amber-600 dark:text-amber-400">{t('nav.upgrade')}</span>
        ) : <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />}
      </div>
      {credits && (
        <div className="mt-2 space-y-1">
          <div className="flex items-center justify-between text-[11px]">
            <span className="text-muted-foreground">{t('nav.bot_credits')}</span>
            <span className={`font-mono tabular-nums ${credits.remaining <= 0 ? 'text-red-500' : 'text-foreground'}`}>
              {fmt(credits.remaining, i18n.language)}
            </span>
          </div>
          <div className="h-1 rounded-full bg-accent overflow-hidden">
            <div className={`h-full rounded-full ${share < 20 ? 'bg-attention' : 'bg-amber-500'}`} style={{ width: `${share}%` }} />
          </div>
        </div>
      )}
      {usage.map(({ key, icon: Icon, used, limit }) => {
        const ratio = used / Math.max(1, limit);
        return (
          <div key={key} className="mt-2 flex items-center gap-2">
            <Icon className="w-3 h-3 text-muted-foreground flex-shrink-0" />
            <div className="flex-1 h-1 bg-accent rounded-full overflow-hidden">
              <div className={`h-full rounded-full ${used >= limit ? 'bg-red-500/80' : ratio >= 0.8 ? 'bg-attention' : 'bg-foreground/40'}`}
                style={{ width: `${Math.min(100, ratio * 100)}%` }} />
            </div>
            <span className="text-[10px] tabular-nums text-muted-foreground">{used}/{limit}</span>
          </div>
        );
      })}
    </Link>
  );
};

const MenuLink = ({ to, icon: Icon, children, onClick }) => (
  <Link to={to} onClick={onClick}
    className="flex items-center gap-2.5 h-8 px-2.5 rounded-md text-[13px] text-foreground/80 hover:text-foreground hover:bg-accent">
    <Icon className="w-4 h-4" />{children}
  </Link>
);

/** The bottom of the sidebar: plan card, then the person with their menu. */
const SidebarFooter = ({ user, plan, isFree, trialing, usage, onLogout, onShortcuts, onItemClick }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const close = () => { setOpen(false); onItemClick?.(); };
  return (
    <div className="flex-shrink-0 px-2.5 pb-2.5 pt-2 space-y-2">
      <PlanCard plan={plan} isFree={isFree} trialing={trialing} usage={usage} onItemClick={onItemClick} />
      <div className="flex items-center gap-1">
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <button type="button" data-testid="sidebar-user"
              className="flex-1 min-w-0 flex items-center gap-2.5 h-9 px-2 rounded-lg text-left hover:bg-accent transition-colors">
              {user?.avatar_url ? (
                <img src={user.avatar_url} alt="" className="w-[22px] h-[22px] rounded-full object-cover flex-shrink-0" />
              ) : (
                <span className="w-[22px] h-[22px] rounded-full bg-accent text-foreground text-[11px] font-semibold flex items-center justify-center flex-shrink-0">
                  {(user?.full_name || t('common.user')).charAt(0)}
                </span>
              )}
              <span className="text-[13px] text-foreground truncate">{user?.full_name}</span>
            </button>
          </PopoverTrigger>
          <PopoverContent side="top" align="start" sideOffset={6} className="w-60 p-1.5">
            <div className="px-2.5 py-2 mb-1 border-b border-border">
              <p className="text-sm font-medium text-foreground truncate">{user?.full_name}</p>
              <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
            </div>
            {/* Bots are reached from the chats; managing them from here (no place of their own). */}
            <MenuLink to="/app/bots" icon={Bot} onClick={close}>{t('nav.manage_bots')}</MenuLink>
            <MenuLink to="/app/subscription" icon={Sparkles} onClick={close}>{t('nav.subscription')}</MenuLink>
            <MenuLink to="/app/settings" icon={Settings} onClick={close}>{t('nav.settings')}</MenuLink>
            <button type="button" onClick={() => { setOpen(false); onShortcuts(); }}
              className="w-full flex items-center gap-2.5 h-8 px-2.5 rounded-md text-[13px] text-foreground/80 hover:text-foreground hover:bg-accent">
              <Keyboard className="w-4 h-4" />{t('nav.shortcuts')}
            </button>
            <button type="button" onClick={() => { setOpen(false); onLogout(); }}
              className="w-full flex items-center gap-2.5 h-8 px-2.5 rounded-md text-[13px] text-foreground/80 hover:text-red-500 hover:bg-red-500/5">
              <LogOut className="w-4 h-4" />{t('nav.logout')}
            </button>
          </PopoverContent>
        </Popover>
        <TutorialCenter side="right" align="end" />
        <LanguageSwitcher variant="icon" side="right" align="end" />
      </div>
    </div>
  );
};

export default SidebarFooter;
